import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import QuizSettingsModal from "./quiz-settings-modal";
import QuestionTypeDropdown from "./question-type-dropdown";
import InlineQuestionEditor from "./inline-question-editor";
import { Question } from "../../schemas/schemas";

export interface QuizSettings {
  time_limit_minutes: number;
  passing_score: number;
  allow_retake: boolean;
  show_correct_answers: boolean;
}

export interface QuizSection {
  id: string;
  title: string;
  content_block?: {
    content_type: string;
    payload?: {
      questions?: Question[];
      time_limit_minutes?: number;
      passing_score?: number;
      allow_retake?: boolean;
      show_correct_answers?: boolean;
    };
  };
}

export interface QuizSectionProps {
  section: QuizSection;
  onUpdateQuizSettings?: (sectionId: string, settings: any) => void;
  onStartEditingQuestion: (sectionId: string, questionType: string) => void;
  onEditQuestionInline: (sectionId: string, question: Question) => void;
  onDeleteQuestion?: (sectionId: string, questionId: string) => void;
  editingQuestion: Question | null;
  editingQuestionSectionId: string | null;
  isEditingExistingQuestion: boolean;
  onSaveInlineQuestion: (question: Question) => void;
  onCancelInlineQuestion: () => void;
  onDeleteInlineQuestion: (questionId: string) => void;
}

export function QuizSection({
  section,
  onUpdateQuizSettings,
  onStartEditingQuestion,
  onEditQuestionInline,
  onDeleteQuestion,
  editingQuestion,
  editingQuestionSectionId,
  isEditingExistingQuestion,
  onSaveInlineQuestion,
  onCancelInlineQuestion,
  onDeleteInlineQuestion,
}: QuizSectionProps) {
  // State for quiz settings modal
  const [isQuizSettingsModalOpen, setIsQuizSettingsModalOpen] = useState(false);

  // Handler for opening quiz settings modal
  const handleOpenQuizSettings = () => {
    setIsQuizSettingsModalOpen(true);
  };

  // Handler for saving quiz settings
  const handleSaveQuizSettings = (settings: any) => {
    if (onUpdateQuizSettings) {
      onUpdateQuizSettings(section.id, settings);
    }
    setIsQuizSettingsModalOpen(false);
  };
  if (section.content_block?.content_type !== "quiz") {
    return null;
  }

  return (
    <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 border-t border-yellow-200 dark:border-yellow-800">
      <div className="flex items-center justify-between mb-4">
        <h4 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
          Câu hỏi trong quiz ({(section.content_block?.payload?.questions || []).length})
        </h4>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleOpenQuizSettings}
            className="bg-yellow-100 border-yellow-300 text-yellow-800 hover:bg-yellow-200"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mr-1"
            >
              <circle cx="12" cy="12" r="3"></circle>
              <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
            </svg>
            Cài đặt
          </Button>
          <QuestionTypeDropdown
            onSelectQuestionType={(questionType) => 
              onStartEditingQuestion(section.id, questionType)
            }
          />
        </div>
      </div>
      
      {/* Quiz Settings */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-xs text-yellow-700 dark:text-yellow-300">
        <div>
          <span className="font-medium">Thời gian:</span> {section.content_block?.payload?.time_limit_minutes || 30} phút
        </div>
        <div>
          <span className="font-medium">Điểm đạt:</span> {section.content_block?.payload?.passing_score || 70}%
        </div>
        <div>
          <span className="font-medium">Cho phép làm lại:</span> {section.content_block?.payload?.allow_retake ? "Có" : "Không"}
        </div>
        <div>
          <span className="font-medium">Hiển thị đáp án:</span> {section.content_block?.payload?.show_correct_answers ? "Có" : "Không"}
        </div>
      </div>
      
      {/* Questions List */}
      <div className="space-y-2">
        {(section.content_block?.payload?.questions || []).map((question: any, questionIndex: number) => {
          // Check if this question is being edited inline
          const isEditingThisQuestion = editingQuestion && 
            editingQuestionSectionId === section.id && 
            editingQuestion.question_id === question.question_id;
          
          return (
            <div key={question.question_id}>
              {/* Show inline editor if this question is being edited */}
              {isEditingThisQuestion ? (
                <InlineQuestionEditor
                  question={editingQuestion}
                  onSave={onSaveInlineQuestion}
                  onCancel={onCancelInlineQuestion}
                  onDelete={onDeleteInlineQuestion}
                />
              ) : (
                <div className="p-3 bg-white dark:bg-gray-800 rounded-lg border border-yellow-200 dark:border-yellow-700">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center mb-2">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 mr-2">
                          Câu {questionIndex + 1}:
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          question.question_type === 'multiple_choice' ? 'bg-blue-100 text-blue-800' :
                          question.question_type === 'boolean' ? 'bg-green-100 text-green-800' :
                          question.question_type === 'text' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {question.question_type === 'multiple_choice' ? 'Trắc nghiệm' :
                           question.question_type === 'boolean' ? 'Đúng/Sai' :
                           question.question_type === 'text' ? 'Tự luận' :
                           question.question_type}
                        </span>
                      </div>
                      <p className="text-sm text-gray-700 dark:text-gray-300 mb-2">
                        {question.question_text}
                      </p>
                      {question.question_type === 'multiple_choice' && question.options && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <div className="font-medium mb-1">Các lựa chọn:</div>
                          <ul className="list-disc list-inside space-y-1">
                            {question.options.map((option: string, optionIndex: number) => (
                              <li key={optionIndex} className={option === question.correct_option ? "text-green-600 dark:text-green-400 font-medium" : ""}>
                                {option} {option === question.correct_option ? "✓" : ""}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {(question.question_type === 'boolean' || question.question_type === 'text') && question.correct_answer && (
                        <div className="text-xs text-gray-600 dark:text-gray-400">
                          <span className="font-medium">Đáp án đúng:</span> {question.correct_answer}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onEditQuestionInline(section.id, question)}
                        className="h-8 w-8 p-0 text-blue-600 hover:bg-blue-100"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                        </svg>
                      </Button>
                      {onDeleteQuestion && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDeleteQuestion(section.id, question.question_id)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"></path>
                            <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                          </svg>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {/* Show inline editor for new questions (when adding new question) - only if not editing existing question */}
        {editingQuestion && 
         editingQuestionSectionId === section.id && 
         editingQuestion.question_id.startsWith('temp_') && 
         !isEditingExistingQuestion && (
          <InlineQuestionEditor
            question={editingQuestion}
            onSave={onSaveInlineQuestion}
            onCancel={onCancelInlineQuestion}
            onDelete={onDeleteInlineQuestion}
          />
        )}
        
        {(section.content_block?.payload?.questions || []).length === 0 && !editingQuestion && (
          <div className="text-center py-4 text-yellow-600 dark:text-yellow-400 text-sm">
            Chưa có câu hỏi nào. Nhấp "Thêm câu hỏi" để bắt đầu.
          </div>
        )}
      </div>

      {/* Quiz Settings Modal */}
      <QuizSettingsModal
        isOpen={isQuizSettingsModalOpen}
        onClose={() => setIsQuizSettingsModalOpen(false)}
        onSave={handleSaveQuizSettings}
        currentSettings={section.content_block?.payload ? {
          time_limit_minutes: section.content_block.payload.time_limit_minutes || 30,
          passing_score: section.content_block.payload.passing_score || 70,
          allow_retake: section.content_block.payload.allow_retake || true,
          show_correct_answers: section.content_block.payload.show_correct_answers || true
        } : undefined}
        translations={{
          title: "Cài đặt Quiz",
          timeLimit: "Thời gian làm bài",
          passingScore: "Điểm đạt",
          allowRetake: "Cho phép làm lại",
          showCorrectAnswers: "Hiển thị đáp án sau khi hoàn thành",
          cancel: "Hủy",
          save: "Lưu",
          minutes: "phút",
          percent: "%"
        }}
      />
    </div>
  );
}
